<template>
  <li :class="liClasses" :data-testid="`deck-tile-${slide.id}`" :data-intercom-target="intercomId" :title="slide.title">
    <p
      v-if="slide.type === 'H'"
      class="deck-tile-slides-header line-clamp-2"
      :title="slide.title"
      :class="{
        'text-sm font-semibold': tileType === DeckTileTypes.Thumbnail,
        'pt-4': tileType === DeckTileTypes.Thumbnail && index != 0,
        'text-lg uppercase': tileType === DeckTileTypes.Icon,
      }"
      data-testid="deck-tile-slides-header"
    >
      {{ slide.title }}
    </p>
    <span v-else-if="slide.type === 'T'" data-testid="deck-tile-type-t" />
    <button
      v-else
      :disabled="disabled"
      :class="buttonClasses"
      :title="disabled ? 'Slide not supported in Meetings' : slide.title"
      @click="$emit('on-slide-selected', index)"
    >
      <template v-if="tileType === DeckTileTypes.Icon">
        <img v-if="slide.type == 'P'" class="slide-icon" src="~/assets/images/deck-icons/pdf-icon.png" draggable="false" />
        <img v-else-if="slide.type == 'I'" class="slide-icon" src="~/assets/images/deck-icons/image-icon.png" draggable="false" />
        <ThreeDIcon v-else-if="slide.type == 'M'" class="slide-icon" draggable="false" />
        <HotspotIcon v-else-if="slide.type == 'S'" class="slide-icon" draggable="false" />
        <DynamicIcon v-else-if="slide.type == 'D'" class="slide-icon" draggable="false" />
        <MeetingIcon v-else-if="slide.type == 'E'" class="slide-icon" draggable="false" />
        <FilmIcon v-else-if="slide.type == 'V'" class="slide-icon" draggable="false" />
        <span class="truncate">{{ slide.title }}</span>
      </template>
      <template v-else-if="tileType === DeckTileTypes.Thumbnail">
        <span class="mr-2 text-xs" v-text="slideNumber" />
        <div class="flex flex-col w-full max-w-full text-ellipsis overflow-hidden">
          <div
            class="mb-1 relative border border-gray-800 rounded-lg aspect-video w-full"
            :class="!['E', 'D'].includes(slide.type) ? 'h-0 w-full pb-[56.25%]' : ''"
          >
            <img
              v-if="!['E', 'D'].includes(slide.type)"
              :src="slideThumbnail"
              draggable="false"
              class="absolute inset-0 h-full w-full object-cover rounded-lg"
            />
            <ThreeDIcon v-if="slide.type == 'M'" class="absolute top-1 right-1 text-white h-5 w-5 drop-shadow" draggable="false" />
            <HotspotIcon v-if="slide.type == 'S'" class="absolute top-1 right-1 text-white h-5 w-5 drop-shadow" draggable="false" />
            <EditorDeckUrlSlideThumbnail v-if="slide.type === 'D'" :slide="slide" class="rounded-lg" />
          </div>
          <span
            class="slide-title text-xs leading-tight break-word w-full max-w-full line-clamp-2 group-hover_line-clamp-none mt-1"
            v-text="slide.title"
          />
        </div>
      </template>
    </button>
  </li>
</template>

<script lang="ts">
export enum DeckTileTypes {
  Thumbnail = 'thumbnail',
  Icon = 'icon',
}
</script>

<script lang="ts" setup>
import type { Slide } from '@/entities/deck'
import ThreeDIcon from '@/assets/images/deck-icons/threed-icon.svg'
import DynamicIcon from '@/assets/images/deck-icons/dynamic-icon.svg'
import MeetingIcon from '@/assets/images/deck-icons/meeting-icon.svg'
import HotspotIcon from '@/components/icons/site/hotspot-360.svg'
import { FilmIcon } from '@heroicons/vue/24/outline'
import type { DeepReadonly } from 'vue'
import PlaceholderDeck from '~/assets/images/deck-placeholder.jpg'
import { getSlideThumbnail } from '~/lib/deck/get-slide-thumbnail'

const props = defineProps({
  selected: {
    type: Boolean,
    default: false,
  },
  index: {
    type: Number,
    default: 0,
  },
  slide: {
    type: Object as PropType<DeepReadonly<Slide>>,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  tileType: {
    type: String,
    default: () => DeckTileTypes.Icon,
  },
  slideNumber: {
    type: Number,
    default: () => 0,
  },
})

const emit = defineEmits(['on-slide-selected'])

type DynamicClasses = Array<string | Record<string, Boolean | undefined | null | string>>

const buttonClasses = computed<DynamicClasses>(() => {
  const { selected, disabled, tileType } = props
  if (tileType === DeckTileTypes.Thumbnail) {
    return [
      'appearance-none',
      'px-1',
      'py-2',
      'w-full',
      'outline-none',
      'slide',
      'rounded-md',
      'transition-colors',
      'group',
      'text-left',
      'flex',
      'flex-row',
      'flex-nowrap',
      'max-w-full',
      {
        'text-white': selected,
        'bg-transparent text-gray-400 hover_text-white': !selected,
        'bg-gray-300': disabled,
      },
    ]
  }
  return ['appearance-none', 'p-0', 'bg-transparent', 'w-full', 'outline-none', 'slide', { 'bg-gray-100': disabled }]
})

const liClasses = computed<DynamicClasses>(() => {
  const { selected, disabled, tileType, slide } = props
  if (tileType === DeckTileTypes.Thumbnail) {
    return [
      'group',
      'mb-2',
      'px-1',
      'rounded-md',
      'transition-colors',
      {
        'bg-[#296189]': selected,
        'hover_bg-[#253C4D]': !selected && slide.type !== 'H',
        'opacity-50': disabled,
      },
    ]
  }
  return [
    'deck-tile-slides',
    'mb-1',
    'p-2',
    'pl-4',
    { 'bg-gray-100 rounded-md': selected },
    { 'bg-gray-100 -mx-2 pl-6 text-gray-500': disabled },
  ]
})

const slideThumbnail = computed<string>(() => {
  return getSlideThumbnail(props.slide)
})

const idMap: Record<string, string> = {
  H: 'header-slide',
  P: 'pdf-slide',
  I: 'image-slide',
  M: '3d-slide',
  S: 'site-slide',
  D: 'dynamic-slide',
  E: 'bam-slide',
  V: 'video-slide',
  T: 'title',
}
const intercomId = computed<string>(() => {
  return idMap[props.slide.type] || ''
})
</script>

<style lang="stylus" scoped>
.deck-tile-slides
  font-size 18px
  -moz-user-select none /* Firefox */
  -ms-user-select none /* Internet Explorer */
  -khtml-user-select none /* KHTML browsers (e.g. Konqueror) */
  -webkit-user-select none /* Chrome, Safari, and Opera */
  -webkit-touch-callout none /* Disable Android and iOS callouts*/

  .slide
    display flex
    align-items center
    white-space nowrap
    text-overflow ellipsis
    overflow hidden

    &-icon
      margin-right 10px
      width 25px
      height 25px
      flex-shrink 0

    span
      font-size 16px

  &-header
    max-width 400px
    color #c3c3c3
    margin-bottom 0
    letter-spacing 0.3px
</style>
